/**
 * The host's view of an event's connections, and the overrides they write over it.
 *
 * `GET    /api/v1/events/:eventIdPublic/connections`                 reads the view.
 * `POST   /api/v1/events/:eventIdPublic/connections/overrides`       writes a batch.
 * `PUT    /api/v1/events/:eventIdPublic/connections/overrides/revert` reverts a batch.
 */
import { z } from "zod";
import {
  EventParticipantIdPublicSchema,
  MatchingPipelineRunIdPublicSchema,
} from "./branded-id.schemas.js";
import { convictionLevelValues } from "./pick-data.schemas.js";
import {
  dropOverrideContentSchema,
  promoteOverrideContentSchema,
} from "./suggestion-override-content.schemas.js";
import { SuggestionOverrideDecision } from "./suggestion-override-decision.schemas.js";

/** The pair a row names: `from` is the reader whose deck it affects. */
const _directedPairFields = {
  fromParticipantIdPublic: EventParticipantIdPublicSchema,
  toParticipantIdPublic: EventParticipantIdPublicSchema,
};

/**
 * One directed override, stated whole: the host's decision carried by the domain content
 * schema, addressed to a pair. A body whose prose contradicts its decision 400s here,
 * because the content schema is discriminated.
 */
export const SuggestionOverrideItemSchema = z.discriminatedUnion("decision", [
  promoteOverrideContentSchema.extend(_directedPairFields),
  dropOverrideContentSchema.extend(_directedPairFields),
]);

export type SuggestionOverrideItemType = z.infer<
  typeof SuggestionOverrideItemSchema
>;

export const SuggestionOverridesBodySchema = z.object({
  items: z.array(SuggestionOverrideItemSchema).min(1),
});

export type SuggestionOverridesBodyType = z.infer<
  typeof SuggestionOverridesBodySchema
>;

/** One directed row to revert; symmetry is again just two items. */
export const SuggestionOverrideRevertItemSchema = z.object(_directedPairFields);

export type SuggestionOverrideRevertItemType = z.infer<
  typeof SuggestionOverrideRevertItemSchema
>;

export const SuggestionOverrideRevertBodySchema = z.object({
  items: z.array(SuggestionOverrideRevertItemSchema).min(1),
});

export type SuggestionOverrideRevertBodyType = z.infer<
  typeof SuggestionOverrideRevertBodySchema
>;

/** What the engine says about the pair; absent when no engine pick backs the row. */
export const ConnectionEnginePickSchema = z.object({
  conviction: z.enum(convictionLevelValues),
  /**
   * The engine's own why-connect paragraph. A published pick always carries one, so a
   * host reviewing a match sees what the engine says even once their own paragraph —
   * `effectiveWhyConnect` — is what ships.
   */
  whyConnect: z.string(),
});

export type ConnectionEnginePickType = z.infer<
  typeof ConnectionEnginePickSchema
>;

const _overrideStateSharedFields = {
  /** The host's own note. Never shown to readers. */
  note: z.string().nullable(),
  /** ISO timestamp when the override was last submitted. */
  submittedAt: z.string(),
  /** Display name of whoever set it; null when the user lookup cannot resolve them. */
  overriddenByName: z.string().nullable(),
};

/**
 * The host's standing decision for one direction. Discriminated on `decision`, so the
 * three prose facts exist only on a promote — a drop publishes nothing to be edited,
 * staled, or authored.
 */
export const ConnectionOverrideStateSchema = z.discriminatedUnion("decision", [
  z.object({
    ..._overrideStateSharedFields,
    decision: z.literal(SuggestionOverrideDecision.PROMOTE),
    /**
     * True when the stored prose differs from the engine text it replaced. Every promote
     * carries a paragraph, so an accepted prefill is not an edit.
     */
    paragraphEdited: z.boolean(),
    /**
     * True when the prose stands over engine text that has since moved on — rewritten,
     * withdrawn, or newly produced for a pair the host added.
     */
    paragraphStale: z.boolean(),
    /**
     * True when the host invented the pair: a promote whose prose replaced no engine
     * text. Provenance, so it survives the engine starting or stopping to produce the
     * pick — the row's author never changes.
     */
    hostOriginated: z.boolean(),
  }),
  z.object({
    ..._overrideStateSharedFields,
    decision: z.literal(SuggestionOverrideDecision.DROP),
  }),
]);

export type ConnectionOverrideStateType = z.infer<
  typeof ConnectionOverrideStateSchema
>;

export const ConnectionRowSchema = z.object({
  fromParticipantIdPublic: EventParticipantIdPublicSchema,
  toParticipantIdPublic: EventParticipantIdPublicSchema,
  fromName: z.string(),
  toName: z.string(),
  fromPhotoUrl: z.string().nullable(),
  toPhotoUrl: z.string().nullable(),
  /** Null for a row that exists only because an override does. */
  enginePick: ConnectionEnginePickSchema.nullable(),
  /** Null for a row nobody has overridden. */
  override: ConnectionOverrideStateSchema.nullable(),
  /**
   * The prose this row would publish: the host's paragraph when one exists, else the
   * engine's. A dropped row publishes nothing at all — the value is then what the
   * editor prefills if the host promotes the pair back.
   */
  effectiveWhyConnect: z.string().nullable(),
});

export type ConnectionRowType = z.infer<typeof ConnectionRowSchema>;

export const ConnectionParticipantSchema = z.object({
  idPublic: EventParticipantIdPublicSchema,
  name: z.string(),
  vip: z.boolean(),
});

export type ConnectionParticipantType = z.infer<
  typeof ConnectionParticipantSchema
>;

export const ConnectionCountsSchema = z.object({
  /** Engine picks surviving drops, plus host-originated promotes. */
  active: z.number().int().nonnegative(),
  promoted: z.number().int().nonnegative(),
  dropped: z.number().int().nonnegative(),
  edited: z.number().int().nonnegative(),
});

export type ConnectionCountsType = z.infer<typeof ConnectionCountsSchema>;

export const ConnectionsViewResponseSchema = z.object({
  eventName: z.string(),
  /** The run being reviewed; null when the event has no finalized picks yet. */
  runIdPublic: MatchingPipelineRunIdPublicSchema.nullable(),
  matches: z.array(ConnectionRowSchema),
  participants: z.array(ConnectionParticipantSchema),
  counts: ConnectionCountsSchema,
});

export type ConnectionsViewResponseType = z.infer<
  typeof ConnectionsViewResponseSchema
>;
